import { useScrollSpy } from '@shared/hooks'
import { Flex, Input, Segmented, Skeleton } from 'antd'
import { SearchNormal1 } from 'iconsax-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CategorySidebar } from './CategorySidebar'
import { PermissionsList } from './PermissionsList'
import { useStyles } from './styles'
import { PermissionsPickerProps, PermissionsStatusFilter } from './types'
import { usePermissionsFilter } from './usePermissionsFilter'

export const PermissionsPicker = ({
  sections,
  value,
  onChange,
  isLoading
}: PermissionsPickerProps) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const selected = useMemo(() => new Set(value ?? []), [value])

  const { search, setSearch, status, setStatus, visible } =
    usePermissionsFilter(sections, selected)

  const visibleKeys = useMemo(() => visible.map(({ key }) => key), [visible])

  const { setContainer, activeKey, scrollTo } = useScrollSpy(visibleKeys)

  const showGroups = useMemo(
    () =>
      new Set(sections.map(({ groupTitle }) => groupTitle).filter(Boolean))
        .size > 1,
    [sections]
  )

  const onToggle = useCallback(
    (key: string, checked: boolean) => {
      const current = value ?? []

      onChange?.(
        checked
          ? [...current, key]
          : current.filter((permission) => permission !== key)
      )
    },
    [value, onChange]
  )

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />

  return (
    <Flex gap={16} align="stretch">
      <CategorySidebar
        sections={visible}
        showGroups={showGroups}
        activeKey={activeKey}
        onSelect={scrollTo}
      />

      <Flex vertical gap={12} flex={1} className={styles.content}>
        <Flex gap={12} wrap>
          <Flex flex={1} className={styles.content}>
            <Input
              allowClear
              value={search}
              prefix={<SearchNormal1 size={16} />}
              placeholder={t('permissions.search_placeholder')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Flex>

          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              {
                label: t('permissions.filter_all'),
                value: PermissionsStatusFilter.ALL
              },
              {
                label: t('permissions.filter_active'),
                value: PermissionsStatusFilter.ACTIVE
              },
              {
                label: t('permissions.filter_inactive'),
                value: PermissionsStatusFilter.INACTIVE
              }
            ]}
          />
        </Flex>

        <PermissionsList
          sections={visible}
          selected={selected}
          showGroups={showGroups}
          setContainer={setContainer}
          onToggle={onToggle}
        />
      </Flex>
    </Flex>
  )
}
